/**
 * Facturation des agrégateurs — la liste et ce qu'on peut en faire.
 *
 * REFONTE DU 27/08/2026 (lot H). Ce qui a changé, et pourquoi :
 *
 * **Le sous-titre ne nomme plus Symphonics ni Survoltage** (item H4). Le lot F permet
 * d'ajouter des agrégateurs : la mention serait devenue fausse le jour même.
 *
 * **Une facture a un cycle de vie** (item H3). Créer produit un brouillon, qu'on relit
 * en aperçu puis qu'on valide. Le numéro n'est attribué qu'à la validation, pour qu'un
 * brouillon jeté ne laisse pas de trou dans la numérotation.
 *
 * **Supprimer une facture émise n'est pas un clic anodin** : elle est numérotée, et son
 * retrait laisse un trou dans la suite. L'écran le dit avant de le faire.
 *
 * Les trois dialogues vivent dans `../components/` : cet écran orchestre, il ne
 * contient plus de formulaire.
 */
import { useState, useEffect, useCallback } from 'react';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import Breadcrumb from '@/shared/components/Breadcrumb';
import { FileText, Plus, Download, Trash2, Eye, Building2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/shared/auth/AuthContext';
import NouvelleFacture from '../components/NouvelleFacture';
import ApercuFacture from '../components/ApercuFacture';
import CoordonneesDestinataires from '../components/CoordonneesDestinataires';
import { EtatRefus, EtatErreur, EtatChargement, estUnRefus } from '@/shared/components/EtatEcran';

const fmtEUR = (v) =>
  v == null ? '—' : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

export default function FacturationPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [factures, setFactures] = useState([]);
  const [chargement, setChargement] = useState(true);
  // L'ERREUR EST CONSERVEE, elle n'est plus avalee. Avant le lot I, un refus de droits,
  // une panne du serveur et un parc reellement vide produisaient la meme phrase :
  // « Aucune facture emise ». Trois situations qui appellent trois actions differentes,
  // reduites a la plus rassurante des trois.
  const [erreur, setErreur] = useState(null);
  const [ecran, setEcran] = useState(null); // 'nouvelle' | 'destinataires' | null
  const [apercu, setApercu] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await api.get('/factures');
      setFactures(r.data || []);
      setErreur(null);
    } catch (e) {
      setFactures([]);
      setErreur(e);
    }
    setChargement(false);
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const supprimer = async (f) => {
    const avertissement =
      f.statut === 'emise'
        ? `La facture ${f.numero} est émise. La supprimer laisse un trou dans la numérotation.\n\nSupprimer quand même ?`
        : 'Supprimer ce brouillon ?';
    if (!window.confirm(avertissement)) return;
    try {
      await api.delete(`/factures/${f.id}`);
      toast.success('Facture supprimée');
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const telecharger = async (f) => {
    try {
      const res = await api.get(`/factures/${f.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Facture ${f.numero}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur PDF');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="facturation-page">
      <div>
        <Breadcrumb items={[{ label: 'Facturation agrégateurs' }]} />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">Facturation agrégateurs</h1>
            <p className="text-sm text-neutral-500 mt-0.5">
              Factures émises aux agrégateurs auxquels votre parc est raccordé.
            </p>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEcran('destinataires')} data-testid="destinataires-btn">
                <Building2 className="w-4 h-4 mr-1" /> Coordonnées
              </Button>
              <Button size="sm" onClick={() => setEcran('nouvelle')} data-testid="new-facture-btn">
                <Plus className="w-4 h-4 mr-1" /> Nouvelle facture
              </Button>
            </div>
          )}
        </div>
      </div>

      {chargement ? (
        <EtatChargement />
      ) : erreur ? (
        /* Trois messages distincts, jamais un seul : un refus n'est pas une panne, et
           une panne n'est pas un parc vide. */
        estUnRefus(erreur) ? <EtatRefus quoi="la facturation des agrégateurs" />
                           : <EtatErreur onReessayer={charger} />
      ) : factures.length === 0 ? (
        <div className="p-12 text-center border border-dashed border-neutral-200 rounded-lg" data-testid="factures-empty">
          <FileText className="w-12 h-12 mx-auto text-neutral-300 mb-3" />
          <p className="text-sm text-neutral-500">Aucune facture émise pour l&apos;instant.</p>
          {isAdmin && <p className="text-[12px] text-neutral-400 mt-1">Cliquez sur « Nouvelle facture » pour en créer une.</p>}
        </div>
      ) : (
        <div className="rounded-lg border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-100">
              <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
                <th className="py-2.5 px-4 font-medium">Statut</th>
                <th className="py-2.5 px-4 font-medium">Numéro</th>
                <th className="py-2.5 px-4 font-medium">Agrégateur</th>
                <th className="py-2.5 px-4 font-medium">Période</th>
                <th className="py-2.5 px-4 font-medium">Émission</th>
                <th className="py-2.5 px-4 font-medium text-right">HT</th>
                <th className="py-2.5 px-4 font-medium text-right">TVA</th>
                <th className="py-2.5 px-4 font-medium text-right">TTC</th>
                <th className="py-2.5 px-4 font-medium">Lignes</th>
                <th className="py-2.5 px-4 font-medium w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {factures.map((f) => {
                const brouillon = f.statut !== 'emise';
                return (
                  <tr key={f.id} className="border-b border-neutral-100 hover:bg-neutral-50/60" data-testid={`facture-row-${f.id}`}>
                    <td className="py-3 px-4">
                      <Badge className={brouillon ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}
                             data-testid={`facture-statut-${f.id}`}>
                        {brouillon ? 'Brouillon' : 'Émise'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 font-mono text-[12px] font-medium">{f.numero || '—'}</td>
                    <td className="py-3 px-4 text-[12px]">{f.agregateur}</td>
                    <td className="py-3 px-4 text-[12px] text-neutral-600">{fmtDate(f.periode_debut)} → {fmtDate(f.periode_fin)}</td>
                    <td className="py-3 px-4 text-[12px] text-neutral-600">{fmtDate(f.date_emission)}</td>
                    <td className="py-3 px-4 text-right text-[12px]">{fmtEUR(f.montant_ht)}</td>
                    <td className="py-3 px-4 text-right text-[12px] text-neutral-500">{fmtEUR(f.montant_tva)}</td>
                    <td className="py-3 px-4 text-right font-semibold text-emerald-700">{fmtEUR(f.montant_ttc)}</td>
                    <td className="py-3 px-4 text-[12px] text-neutral-500">{f.lignes?.length || 0}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        {brouillon ? (
                          <button onClick={() => setApercu(f)} title="Aperçu et validation"
                                  className="p-1 rounded hover:bg-neutral-100 text-neutral-600"
                                  data-testid={`facture-apercu-${f.id}`}>
                            <Eye className="w-4 h-4" />
                          </button>
                        ) : (
                          <button onClick={() => telecharger(f)} title="Télécharger le PDF"
                                  className="p-1 rounded hover:bg-neutral-100 text-neutral-600"
                                  data-testid={`facture-pdf-${f.id}`}>
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => supprimer(f)} title="Supprimer"
                                  className="p-1 rounded hover:bg-red-50 text-neutral-300 hover:text-red-500"
                                  data-testid={`facture-delete-${f.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {factures.some((f) => f.statut !== 'emise') && (
        <p className="text-[12px] text-neutral-500 flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5 text-amber-500" />
          Les brouillons n&apos;ont pas encore de numéro : ouvrez l&apos;aperçu pour les relire et les valider.
        </p>
      )}

      {ecran === 'nouvelle' && (
        <NouvelleFacture
          onClose={() => setEcran(null)}
          onCreee={(facture) => { setEcran(null); charger(); setApercu(facture); }}
        />
      )}
      {ecran === 'destinataires' && <CoordonneesDestinataires onClose={() => setEcran(null)} />}
      {apercu && (
        <ApercuFacture
          facture={apercu}
          onClose={() => { setApercu(null); charger(); }}
          onValidee={() => { setApercu(null); charger(); }}
        />
      )}
    </div>
  );
}
